import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { RefreshCw, Sparkles, Target, Settings, Send, AlertTriangle, CheckCircle2, Bot, ListChecks, Film, Globe, BarChart3 } from 'lucide-react';
import api from '../../services/api';
import Acoes from './GestorTrafegoAcoes';
import Criativos from './GestorTrafegoCriativos';
import Google from './GestorTrafegoGoogle';
import GoogleAds from './GestorTrafegoGoogleAds';
import GooglePerfil from './GestorTrafegoGooglePerfil';
import Desempenho from './GestorTrafegoDesempenho';

// Gestor de Tráfego (Meta Ads) — Fase 1: painel investimento → contato → visita → venda + análise do Claude;
// Fase 2: conversões devolvidas à Meta (CAPI); Fase 3: agente especialista; Fases 3b/4/5 (aba Ações): propostas de
// mudança na conta, campanha nova criada pausada e regras automáticas — tudo com aprovação de admin e teto.

function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}

const fmtBRL = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: Number(v) >= 1000 ? 0 : 2 }));
const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR'));
const fmtData = (s) => (s ? new Date(s).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDia = (s) => { const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}` : s; };
const erroMsg = (err) => err?.response?.data?.error || err?.message;

const STATUS_CAMPANHA = {
  ACTIVE: ['Ativa', 'bg-green-100 text-green-700'],
  PAUSED: ['Pausada', 'bg-gray-100 text-gray-600'],
  CAMPAIGN_PAUSED: ['Pausada', 'bg-gray-100 text-gray-600'],
  ARCHIVED: ['Arquivada', 'bg-gray-100 text-gray-500'],
  DELETED: ['Excluída', 'bg-gray-100 text-gray-500'],
  WITH_ISSUES: ['Com problema', 'bg-red-100 text-red-700'],
  IN_PROCESS: ['Em análise', 'bg-yellow-100 text-yellow-700'],
};
const STATUS_CAPI = {
  pendente: ['Na fila', 'bg-blue-100 text-blue-700'],
  enviado: ['Enviado', 'bg-green-100 text-green-700'],
  teste: ['Enviado (teste)', 'bg-purple-100 text-purple-700'],
  erro: ['Erro', 'bg-red-100 text-red-700'],
  ignorado: ['Ignorado', 'bg-gray-100 text-gray-600'],
};
const EVENTO_LABEL = { visita_agendada: 'Visita agendada', contrato_assinado: 'Contrato assinado' };

function Pill({ par }) {
  const [label, cls] = par || ['—', 'bg-gray-100 text-gray-500'];
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
}

function Kpi({ label, value, sub, destaque }) {
  return (
    <div className={`rounded-xl border p-4 ${destaque ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-100'}`}>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// Markdown simples do relatório/agente (títulos, listas, **negrito**, tabelas, citações) — sem dependência nova
function Markdown({ texto }) {
  const inline = (t) => String(t).split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : p));
  const blocos = [];
  let lista = null;
  let tabela = null;
  String(texto || '').split('\n').forEach((linha) => {
    const l = linha.trimEnd();
    if (/^\s*\|.*\|\s*$/.test(l)) {
      lista = null;
      const cels = l.trim().slice(1, -1).split('|').map((c) => c.trim());
      if (cels.every((c) => /^:?-{2,}:?$/.test(c))) return;   // linha separadora
      if (!tabela) { tabela = { linhas: [] }; blocos.push(tabela); }
      tabela.linhas.push(cels);
      return;
    }
    tabela = null;
    const item = l.match(/^\s*(?:[-*]|\d+\.)\s+(.*)$/);
    if (item) {
      if (!lista) { lista = { ordenada: /^\s*\d+\./.test(l), itens: [] }; blocos.push(lista); }
      lista.itens.push(item[1]);
      return;
    }
    lista = null;
    if (/^#{1,4}\s/.test(l)) blocos.push({ titulo: l.replace(/^#+\s/, ''), nivel: l.match(/^#+/)[0].length });
    else if (/^\s*>\s?/.test(l)) blocos.push({ citacao: l.replace(/^\s*>\s?/, '') });
    else if (l.trim()) blocos.push({ p: l });
  });
  return (
    <div className="space-y-2 text-sm text-gray-700 leading-relaxed">
      {blocos.map((b, i) => {
        if (b.titulo) return <h3 key={i} className={`${b.nivel <= 2 ? 'text-base' : 'text-sm'} font-semibold text-gray-900 pt-3`}>{inline(b.titulo)}</h3>;
        if (b.itens) {
          const Tag = b.ordenada ? 'ol' : 'ul';
          return <Tag key={i} className={`${b.ordenada ? 'list-decimal' : 'list-disc'} pl-5 space-y-1`}>{b.itens.map((t, j) => <li key={j}>{inline(t)}</li>)}</Tag>;
        }
        if (b.linhas) return (
          <div key={i} className="overflow-x-auto"><table className="text-xs border border-gray-100 my-1">
            <tbody>{b.linhas.map((cels, j) => (
              <tr key={j} className={j === 0 ? 'bg-gray-50 font-semibold' : 'border-t border-gray-100'}>{cels.map((c, k) => <td key={k} className="px-2 py-1 align-top">{inline(c)}</td>)}</tr>
            ))}</tbody>
          </table></div>
        );
        if (b.citacao !== undefined) return <blockquote key={i} className="border-l-4 border-blue-200 bg-blue-50 px-3 py-2 rounded-r text-gray-800">{inline(b.citacao)}</blockquote>;
        return <p key={i}>{inline(b.p)}</p>;
      })}
    </div>
  );
}

// ── Painel ──────────────────────────────────────────────────────────────────
function Painel({ dias, configurado }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['trafego-funil', dias],
    queryFn: () => api.get('/trafego/funil', { params: { dias } }).then(r => r.data.data),
  });
  const { data: emps } = useQuery({ queryKey: ['trafego-emps'], queryFn: () => api.get('/trafego/empreendimentos').then(r => r.data.data) });
  const mudarEmp = useMutation({
    mutationFn: ({ campaignId, empreendimento_id }) => api.put(`/trafego/campanhas/${campaignId}/empreendimento`, { empreendimento_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trafego-funil'] }),
    onError: (err) => alert('Erro: ' + erroMsg(err)),
  });

  if (isLoading) return <div className="text-gray-500 p-6">Carregando…</div>;
  if (error) return <div className="text-red-600 p-6">Erro: {erroMsg(error)}</div>;
  const t = data.totais;
  const q = data.qualidade_dados;

  return (
    <div className="space-y-6">
      {!configurado && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
          A conta de anúncios ainda não foi conectada — configure na aba <strong>Configuração</strong> para os números de investimento aparecerem.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Investimento" value={fmtBRL(t.investimento)} sub={`${fmtNum(t.impressoes)} impressões · CTR ${t.ctr ?? '—'}%`} destaque />
        <Kpi label="Conversas / leads (Meta)" value={fmtNum(t.conversas_meta + t.leads_meta)} sub={`custo ${fmtBRL(t.custo_conversa_meta)}`} />
        <Kpi label="Visitas agendadas (de anúncio)" value={fmtNum(t.visitas)} sub={`custo por visita ${fmtBRL(t.custo_visita)}`} />
        <Kpi label="Vendas (de anúncio)" value={fmtNum(t.vendas)} sub={t.vendas ? `custo por venda ${fmtBRL(t.custo_venda)} · VGV ${fmtBRL(t.vgv)}` : 'nenhuma no período'} />
      </div>

      {data.por_plataforma && (data.por_plataforma.google.investimento > 0 || data.por_plataforma.google.contatos > 0) && (
        <div className="grid md:grid-cols-2 gap-3">
          {[['meta', 'Meta (Facebook/Instagram)'], ['google', 'Google Ads']].map(([k, rot]) => {
            const p = data.por_plataforma[k];
            return (
              <div key={k} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-sm">
                <div className="font-semibold text-gray-700 mb-2">{rot}</div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div><div className="text-xs text-gray-500">Investimento</div><div className="font-bold">{fmtBRL(p.investimento)}</div></div>
                  <div><div className="text-xs text-gray-500">Contatos</div><div className="font-bold">{fmtNum(p.contatos)}</div></div>
                  <div><div className="text-xs text-gray-500">Visitas</div><div className="font-bold">{fmtNum(p.visitas)}</div><div className="text-[11px] text-gray-400">{fmtBRL(p.custo_visita)}</div></div>
                  <div><div className="text-xs text-gray-500">Vendas</div><div className="font-bold">{fmtNum(p.vendas)}</div><div className="text-[11px] text-gray-400">{fmtBRL(p.custo_venda)}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.serie?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Investimento × conversas por dia (Meta)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.serie}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="data" tickFormatter={fmtDia} fontSize={11} />
              <YAxis yAxisId="r" fontSize={11} tickFormatter={(v) => `R$${v}`} />
              <YAxis yAxisId="c" orientation="right" fontSize={11} />
              <Tooltip labelFormatter={fmtDia} formatter={(v, n) => (n === 'Investimento' ? fmtBRL(v) : v)} />
              <Line yAxisId="r" type="monotone" dataKey="investimento" name="Investimento" stroke="#1B3F5E" strokeWidth={2} dot={false} />
              <Line yAxisId="c" type="monotone" dataKey="conversas" name="Conversas/leads" stroke="#F5A623" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <h3 className="text-sm font-semibold text-gray-700 px-4 pt-4">Por empreendimento</h3>
        <table className="w-full text-sm mt-2">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Empreendimento</th><th className="text-right px-3">Investimento</th>
              <th className="text-right px-3">Conversas</th><th className="text-right px-3">Contatos CRM</th>
              <th className="text-right px-3">Visitas</th><th className="text-right px-3">Vendas</th>
              <th className="text-right px-3">Custo/visita</th><th className="text-right px-4">Custo/venda</th>
            </tr>
          </thead>
          <tbody>
            {data.empreendimentos.map((e) => (
              <tr key={e.empreendimento_id || 'sem'} className="border-t border-gray-50">
                <td className="px-4 py-2 font-medium text-gray-800">{e.nome}</td>
                <td className="text-right px-3">{fmtBRL(e.investimento)}</td>
                <td className="text-right px-3">{fmtNum(e.conversas_meta + e.leads_meta)}</td>
                <td className="text-right px-3">{fmtNum(e.contatos)}</td>
                <td className="text-right px-3">{fmtNum(e.visitas)}</td>
                <td className="text-right px-3">{fmtNum(e.vendas)}</td>
                <td className="text-right px-3">{fmtBRL(e.custo_visita)}</td>
                <td className="text-right px-4">{fmtBRL(e.custo_venda)}</td>
              </tr>
            ))}
            {!data.empreendimentos.length && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">Sem dados no período</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <h3 className="text-sm font-semibold text-gray-700 px-4 pt-4">Por campanha</h3>
        <table className="w-full text-sm mt-2">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Campanha</th><th className="text-left px-3">Empreendimento</th>
              <th className="text-right px-3">Orçam./dia</th><th className="text-right px-3">Investimento</th>
              <th className="text-right px-3">CTR</th><th className="text-right px-3">Conversas</th>
              <th className="text-right px-3">Custo/conversa</th><th className="text-right px-3">Visitas</th>
              <th className="text-right px-4">Vendas</th>
            </tr>
          </thead>
          <tbody>
            {data.campanhas.map((c) => (
              <tr key={c.campaign_id || 'sem'} className="border-t border-gray-50 align-top">
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-800">
                    <span className={`mr-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${c.plataforma === 'google' ? 'bg-blue-50 text-blue-700' : 'bg-indigo-50 text-indigo-700'}`}>{c.plataforma === 'google' ? 'Google' : 'Meta'}</span>{c.nome}
                  </div>
                  <div className="mt-1"><Pill par={STATUS_CAMPANHA[c.status] || (c.status ? [c.status, 'bg-gray-100 text-gray-600'] : null)} /></div>
                </td>
                <td className="px-3 py-2">
                  {c.campaign_id ? (
                    <select
                      className="border border-gray-200 rounded-md text-xs px-2 py-1 max-w-[180px]"
                      value={c.empreendimento_id || ''}
                      onChange={(ev) => mudarEmp.mutate({ campaignId: c.campaign_id, empreendimento_id: ev.target.value || null })}
                    >
                      <option value="">— definir —</option>
                      {(emps || []).map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
                    </select>
                  ) : '—'}
                </td>
                <td className="text-right px-3 py-2">{fmtBRL(c.orcamento_diario)}</td>
                <td className="text-right px-3 py-2">{fmtBRL(c.investimento)}</td>
                <td className="text-right px-3 py-2">{c.ctr != null ? `${c.ctr}%` : '—'}</td>
                <td className="text-right px-3 py-2">{c.plataforma === 'google' ? <span title="conversões contadas pelo Google">{fmtNum(c.conversoes_google || 0)}<span className="text-[10px] text-gray-400"> G</span></span> : fmtNum(c.conversas_meta + c.leads_meta)}</td>
                <td className="text-right px-3 py-2">{c.plataforma === 'google' ? fmtBRL(c.conversoes_google ? c.investimento / c.conversoes_google : null) : fmtBRL(c.custo_conversa_meta)}</td>
                <td className="text-right px-3 py-2">{fmtNum(c.visitas)}</td>
                <td className="text-right px-4 py-2">{fmtNum(c.vendas)}{c.vgv ? <div className="text-xs text-gray-400">{fmtBRL(c.vgv)}</div> : null}</td>
              </tr>
            ))}
            {!data.campanhas.length && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">Sem campanhas no período</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-xs text-gray-600">
        <strong>Rastreamento no período:</strong> {q.visitas_atribuidas_a_anuncio} de {q.visitas_no_periodo} visitas e {q.vendas_atribuidas_a_anuncio} de {q.vendas_no_periodo} vendas
        foram ligadas a um anúncio (pelo telefone do cliente). O restante veio de outras origens (indicação, corretor, orgânico) ou chegou antes do
        rastreamento começar. Regra: vale o último anúncio que o cliente clicou antes da visita/venda.
      </div>
    </div>
  );
}

// ── Análises do Claude ─────────────────────────────────────────────────────
function Analises() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['trafego-relatorios'], queryFn: () => api.get('/trafego/relatorios').then(r => r.data.data) });
  const gerar = useMutation({
    mutationFn: (dias) => api.post('/trafego/relatorios', { dias }, { timeout: 330000 }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trafego-relatorios'] }),
    onError: (err) => alert('Não foi possível gerar: ' + erroMsg(err)),
  });
  const [aberto, setAberto] = useState(null);
  const lista = data || [];
  const atual = aberto ? lista.find(r => r.id === aberto) : lista[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => gerar.mutate(7)} disabled={gerar.isPending}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
        >
          <Sparkles size={16} /> {gerar.isPending ? 'Analisando… (até 2 min)' : 'Gerar análise dos últimos 7 dias'}
        </button>
        <span className="text-xs text-gray-500">Toda segunda às 8h sai uma análise automática. A IA só recomenda — nada é alterado nos anúncios.</span>
      </div>
      {isLoading && <div className="text-gray-500">Carregando…</div>}
      {!isLoading && !lista.length && <div className="bg-white rounded-xl border border-gray-100 p-6 text-gray-500 text-sm">Nenhuma análise ainda.</div>}
      {atual && (
        <div className="grid md:grid-cols-[220px_1fr] gap-4">
          <div className="space-y-1">
            {lista.map((r) => (
              <button key={r.id} onClick={() => setAberto(r.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs ${r.id === atual.id ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50 text-gray-600'}`}>
                <div className="font-semibold">{fmtDia(r.periodo_inicio)} a {fmtDia(r.periodo_fim)}</div>
                <div>{r.origem === 'semanal' ? 'Automática (segunda)' : `Pedida por ${r.gerado_por_nome || '—'}`} · {fmtData(r.created_at)}</div>
              </button>
            ))}
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <Markdown texto={atual.texto} />
            <p className="text-[11px] text-gray-400 mt-4">Gerado por {atual.modelo} com os números do painel. Confira os números antes de mexer em orçamento.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Conversões (CAPI) ──────────────────────────────────────────────────────
function Conversoes({ cfg }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['trafego-conversoes'], queryFn: () => api.get('/trafego/conversoes').then(r => r.data.data) });
  const processar = useMutation({
    mutationFn: () => api.post('/trafego/conversoes/processar'),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['trafego-conversoes'] }); const d = r.data.data; alert(`${d.novos} nova(s) na fila. ${d.motivo || `${d.enviados} enviada(s), ${d.erros} erro(s).`}`); },
    onError: (err) => alert('Erro: ' + erroMsg(err)),
  });
  const modo = data?.modo || cfg?.capi_modo || 'desligado';
  const banner = {
    desligado: ['bg-gray-50 border-gray-200 text-gray-700', 'Envio DESLIGADO — o CRM monta a fila do que seria enviado, mas nada sai para a Meta.'],
    teste: ['bg-purple-50 border-purple-200 text-purple-800', 'Modo TESTE — os eventos aparecem em "Eventos de teste" do Gerenciador de Eventos e não afetam a otimização.'],
    producao: ['bg-green-50 border-green-200 text-green-800', 'Envio LIGADO — visitas e contratos de clientes vindos de anúncio voltam para a Meta otimizar as campanhas.'],
  }[modo];

  return (
    <div className="space-y-4">
      <div className={`border rounded-xl p-4 text-sm ${banner[0]}`}>{banner[1]}</div>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => processar.mutate()} disabled={processar.isPending}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60">
          <Send size={16} /> {processar.isPending ? 'Processando…' : 'Processar agora'}
        </button>
        <span className="text-xs text-gray-500">Roda sozinho a cada 30 min. Só entram eventos dos últimos 7 dias (limite da Meta).</span>
      </div>
      {data && (
        <div className="flex flex-wrap gap-2 text-xs">
          {data.origens.map((o) => <span key={o.canal} className="bg-white border border-gray-100 rounded-lg px-3 py-1">{o.canal === 'whatsapp_anuncio' ? 'Contatos via anúncio de WhatsApp' : 'Leads de formulário'}: <strong>{o.n}</strong></span>)}
          {data.contagem.map((c) => <span key={c.status} className="bg-white border border-gray-100 rounded-lg px-3 py-1"><Pill par={STATUS_CAPI[c.status]} /> <strong>{c.n}</strong></span>)}
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-2">Quando</th><th className="text-left px-3">Evento</th><th className="text-left px-3">Origem</th><th className="text-right px-3">Valor</th><th className="text-left px-3">Status</th><th className="text-left px-4">Detalhe</th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-4 py-6 text-gray-400">Carregando…</td></tr>}
            {(data?.eventos || []).map((e) => (
              <tr key={e.id} className="border-t border-gray-50 align-top">
                <td className="px-4 py-2 whitespace-nowrap">{fmtData(e.event_time)}</td>
                <td className="px-3 py-2">{EVENTO_LABEL[e.evento] || e.evento}<div className="text-xs text-gray-400">{e.event_name}</div></td>
                <td className="px-3 py-2 text-xs">{e.canal === 'whatsapp_anuncio' ? 'Anúncio WhatsApp' : 'Formulário'}<div className="text-gray-400">{e.titulo_anuncio || ''}</div></td>
                <td className="text-right px-3 py-2">{e.valor ? fmtBRL(e.valor) : '—'}</td>
                <td className="px-3 py-2"><Pill par={STATUS_CAPI[e.status]} /></td>
                <td className="px-4 py-2 text-xs text-gray-500 max-w-xs break-words">{e.status === 'erro' || e.status === 'ignorado' ? e.resposta : e.enviado_em ? `enviado ${fmtData(e.enviado_em)}` : ''}</td>
              </tr>
            ))}
            {!isLoading && !data?.eventos?.length && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Nenhuma conversão ainda — aparecem quando um cliente que veio de anúncio agenda visita ou assina contrato.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Público (ficha por empreendimento + quem compra + região) ─────────────
const CAMPOS_FICHA = [
  ['posicionamento', 'Posicionamento', 'O que é e para quem é, em uma ou duas frases'],
  ['nichos', 'Nichos-alvo', 'Em ordem de prioridade'],
  ['gatilhos', 'Gatilhos de compra', ''],
  ['objecoes', 'Objeções e respostas', ''],
  ['segmentacao', 'Segmentação na Meta', ''],
  ['criativos', 'Criativos e textos', ''],
  ['observacoes', 'Observações', ''],
];

function Contagem({ titulo, itens, total }) {
  if (!itens?.length) return null;
  return (
    <div>
      <p className="text-xs font-semibold text-gray-600 mb-1">{titulo}</p>
      <div className="flex flex-wrap gap-1">
        {itens.map((i) => <span key={i.valor} className="text-xs bg-gray-100 rounded-full px-2 py-0.5">{i.valor} <strong>{i.n}</strong>{total ? <span className="text-gray-400"> ({Math.round((i.n / total) * 100)}%)</span> : null}</span>)}
      </div>
    </div>
  );
}

function QuemCompra({ empId }) {
  const { data, isLoading } = useQuery({ queryKey: ['trafego-compradores', empId], queryFn: () => api.get(`/trafego/publico/${empId}/compradores`).then(r => r.data.data) });
  if (isLoading) return <div className="text-xs text-gray-400">Carregando compradores…</div>;
  const c = data?.compradores; const e = data?.estoque;
  return (
    <div className="space-y-3">
      {e && <p className="text-xs text-gray-600"><strong>Estoque:</strong> {e.disponiveis} disponíveis de {e.total_unidades}{e.preco_tabela_disponiveis ? ` · tabela ${fmtBRL(e.preco_tabela_disponiveis.minimo)} a ${fmtBRL(e.preco_tabela_disponiveis.maximo)}` : ''}{e.area_disponiveis_m2 ? ` · ${e.area_disponiveis_m2.minima}–${e.area_disponiveis_m2.maxima} m²` : ''}</p>}
      {!c?.contratos ? <p className="text-xs text-gray-400">Sem contratos assinados no CRM para este empreendimento.</p> : (
        <>
          <p className="text-xs text-gray-600"><strong>{c.contratos} contratos</strong> · {c.pessoa_juridica} por empresa/holding · {c.de_maringa_pct}% moram em Maringá{c.ticket ? ` · ticket médio ${fmtBRL(c.ticket.medio)}` : ''}</p>
          <Contagem titulo="Profissões" itens={c.profissoes} />
          <Contagem titulo="Estado civil" itens={c.estado_civil} />
          <Contagem titulo="Cidades" itens={c.cidades} total={c.contratos} />
          <Contagem titulo="Bairros (Maringá)" itens={c.bairros_maringa} />
          <Contagem titulo="Faixa de preço pago" itens={c.ticket?.faixas} total={c.contratos} />
          <p className="text-[11px] text-gray-400">Só números agregados — nenhum dado pessoal vai para a IA.</p>
        </>
      )}
    </div>
  );
}

function FichaEmpreendimento({ ficha }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => Object.fromEntries(CAMPOS_FICHA.map(([k]) => [k, ficha[k] || ''])));
  const [aberto, setAberto] = useState(false);
  const salvar = useMutation({
    mutationFn: () => api.put(`/trafego/publico/${ficha.id}`, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trafego-publico'] }); alert('Ficha salva.'); },
    onError: (err) => alert('Erro: ' + erroMsg(err)),
  });
  const rascunho = useMutation({
    mutationFn: () => api.post(`/trafego/publico/${ficha.id}/rascunho`, {}, { timeout: 330000 }),
    onSuccess: (r) => { setForm(Object.fromEntries(CAMPOS_FICHA.map(([k]) => [k, r.data.data[k] || '']))); qc.invalidateQueries({ queryKey: ['trafego-publico'] }); },
    onError: (err) => alert('Não foi possível gerar: ' + erroMsg(err)),
  });
  const preenchida = CAMPOS_FICHA.some(([k]) => ficha[k]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <button onClick={() => setAberto((v) => !v)} className="w-full flex flex-wrap items-center justify-between gap-2 px-5 py-4 text-left">
        <div>
          <div className="font-semibold text-gray-800">{ficha.nome}</div>
          <div className="text-xs text-gray-500">{ficha.bairro || ''}{ficha.atualizado_em ? ` · ficha atualizada ${fmtData(ficha.atualizado_em)} por ${ficha.atualizado_por || '—'}` : ''}</div>
        </div>
        <div className="flex items-center gap-2">
          {ficha.rascunho_ia && <span className="text-xs bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 font-semibold">Rascunho da IA — revisar</span>}
          {!preenchida && <span className="text-xs bg-yellow-100 text-yellow-700 rounded-full px-2 py-0.5 font-semibold">Sem ficha</span>}
          <span className="text-gray-400 text-sm">{aberto ? '▲' : '▼'}</span>
        </div>
      </button>
      {aberto && (
        <div className="border-t border-gray-100 p-5 grid lg:grid-cols-[320px_1fr] gap-6">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Quem compra (contratos assinados)</h4>
            <QuemCompra empId={ficha.id} />
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => rascunho.mutate()} disabled={rascunho.isPending} className="flex items-center gap-2 border border-purple-200 text-purple-700 px-3 py-1.5 rounded-lg text-sm disabled:opacity-60">
                <Sparkles size={14} /> {rascunho.isPending ? 'A IA está montando… (até 1 min)' : preenchida ? 'Melhorar com IA' : 'Gerar rascunho com IA'}
              </button>
              <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-60">{salvar.isPending ? 'Salvando…' : 'Salvar (revisado)'}</button>
            </div>
            {CAMPOS_FICHA.map(([k, label, dica]) => (
              <label key={k} className="block">
                <span className="text-xs font-medium text-gray-600">{label}{dica ? <span className="text-gray-400"> — {dica}</span> : null}</span>
                <textarea className="input-field mt-1 font-normal" rows={k === 'posicionamento' ? 2 : 5} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Regiao({ texto, padrao }) {
  const qc = useQueryClient();
  const [valor, setValor] = useState(texto || '');
  const [aberto, setAberto] = useState(false);
  const salvar = useMutation({
    mutationFn: () => api.put('/trafego/publico/regiao', { texto: valor }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trafego-publico'] }); alert('Contexto da região salvo.'); },
    onError: (err) => alert('Erro: ' + erroMsg(err)),
  });
  const rascunho = valor.startsWith('RASCUNHO');
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <button onClick={() => setAberto((v) => !v)} className="w-full flex items-center justify-between px-5 py-4 text-left">
        <div>
          <div className="font-semibold text-gray-800">Maringá e região</div>
          <div className="text-xs text-gray-500">O que o agente sabe do mercado local — bairros, cidades, economia, calendário.</div>
        </div>
        <div className="flex items-center gap-2">
          {rascunho && <span className="text-xs bg-yellow-100 text-yellow-700 rounded-full px-2 py-0.5 font-semibold">Rascunho — revisar</span>}
          <span className="text-gray-400 text-sm">{aberto ? '▲' : '▼'}</span>
        </div>
      </button>
      {aberto && (
        <div className="border-t border-gray-100 p-5 space-y-3">
          <p className="text-xs text-gray-500">Corrija e complete com o que você sabe do mercado (ex.: bairros onde mora quem compra, concorrentes, épocas boas de venda). Tire a palavra RASCUNHO do início quando revisar.</p>
          <textarea className="input-field font-normal" rows={18} value={valor} onChange={(e) => setValor(e.target.value)} />
          <div className="flex gap-2">
            <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-60">Salvar</button>
            <button onClick={() => setValor(padrao)} className="border border-gray-200 px-3 py-1.5 rounded-lg text-sm">Voltar ao texto original</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Publico() {
  const { data, isLoading, error } = useQuery({ queryKey: ['trafego-publico'], queryFn: () => api.get('/trafego/publico').then(r => r.data.data) });
  if (isLoading) return <div className="text-gray-500 p-6">Carregando…</div>;
  if (error) return <div className="text-red-600 p-6">Erro: {erroMsg(error)}</div>;
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">O agente usa estas fichas e o perfil real de quem comprou para decidir para quem anunciar cada empreendimento. Gere o rascunho com a IA e revise com a equipe comercial.</p>
      <Regiao texto={data.regiao} padrao={data.regiao_padrao} />
      {data.fichas.map((f) => <FichaEmpreendimento key={f.id} ficha={f} />)}
    </div>
  );
}

// ── Gestor IA (conversa com o agente) ───────────────────────────────────────
const SUGESTOES = [
  'Para qual público devo anunciar as unidades restantes do Lumière?',
  'Monte 3 nichos para o Ingarden com um texto de anúncio para cada.',
  'Como foram os anúncios nos últimos 7 dias? O que eu mudaria?',
  'Quais anúncios estão gastando mais e trazendo menos conversas?',
  'Monte uma campanha de WhatsApp do Lumière para eu aprovar, reaproveitando os criativos que funcionaram.',
];

function GestorIA() {
  const qc = useQueryClient();
  const [conversaId, setConversaId] = useState(null);
  const [texto, setTexto] = useState('');
  const [pendente, setPendente] = useState(null);
  const { data: conversas } = useQuery({ queryKey: ['trafego-conversas'], queryFn: () => api.get('/trafego/agente/conversas').then(r => r.data.data) });
  const { data: atual } = useQuery({
    queryKey: ['trafego-conversa', conversaId], enabled: !!conversaId,
    queryFn: () => api.get(`/trafego/agente/conversas/${conversaId}`).then(r => r.data.data),
  });
  const perguntar = useMutation({
    mutationFn: (t) => api.post('/trafego/agente/perguntar', { conversa_id: conversaId, texto: t }, { timeout: 330000 }),
    onMutate: (t) => { setPendente(t); setTexto(''); },
    onSuccess: (r) => {
      const id = r.data.data.conversa_id;
      setConversaId(id);
      qc.invalidateQueries({ queryKey: ['trafego-conversa', id] });
      qc.invalidateQueries({ queryKey: ['trafego-conversas'] });
    },
    onError: (err, t) => { setTexto(t); alert('O agente não conseguiu responder: ' + erroMsg(err)); },
    onSettled: () => setPendente(null),
  });
  const enviar = (t) => { const v = (t ?? texto).trim(); if (v && !perguntar.isPending) perguntar.mutate(v); };
  const msgs = conversaId ? atual?.mensagens || [] : [];

  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-4">
      <div className="space-y-1">
        <button onClick={() => setConversaId(null)} className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-primary hover:bg-blue-50">+ Nova conversa</button>
        {(conversas || []).map((c) => (
          <button key={c.id} onClick={() => setConversaId(c.id)}
            className={`w-full text-left px-3 py-2 rounded-lg text-xs ${c.id === conversaId ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50 text-gray-600'}`}>
            <div className="font-medium line-clamp-2">{c.titulo}</div>
            <div className="text-gray-400">{fmtData(c.updated_at)}</div>
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-[520px]">
        <div className="flex-1 p-5 space-y-4 overflow-y-auto">
          {!msgs.length && !pendente && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Sou o gestor de tráfego da inBase: conheço quem compra cada empreendimento, o estoque que falta vender e o mercado de Maringá e região. Posso propor mudanças (pausar, orçamento, campanha nova), mas nada é executado sem aprovação de um admin na aba Ações.</p>
              <div className="flex flex-wrap gap-2">
                {SUGESTOES.map((s) => <button key={s} onClick={() => enviar(s)} className="text-xs border border-gray-200 rounded-full px-3 py-1 hover:bg-gray-50 text-left">{s}</button>)}
              </div>
            </div>
          )}
          {msgs.map((m) => (
            m.papel === 'user'
              ? <div key={m.id} className="flex justify-end"><div className="bg-primary text-white text-sm rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%] whitespace-pre-wrap">{m.texto}</div></div>
              : <div key={m.id} className="max-w-full"><Markdown texto={m.texto} /></div>
          ))}
          {pendente && (
            <>
              <div className="flex justify-end"><div className="bg-primary/80 text-white text-sm rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%] whitespace-pre-wrap">{pendente}</div></div>
              <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Analisando os dados… (pode levar até 1 minuto)</div>
            </>
          )}
        </div>
        <div className="border-t border-gray-100 p-3 flex gap-2">
          <textarea className="input-field font-normal flex-1" rows={2} placeholder="Pergunte sobre públicos, campanhas, criativos, orçamento…"
            value={texto} onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }} />
          <button onClick={() => enviar()} disabled={perguntar.isPending || !texto.trim()} className="bg-primary text-white px-4 rounded-lg text-sm font-medium disabled:opacity-50"><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}

// ── Configuração (admin) ───────────────────────────────────────────────────
function Configuracao({ cfg }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    ad_account_id: cfg?.ad_account_id || '', access_token: '', dataset_id: cfg?.dataset_id || '',
    waba_id: cfg?.waba_id || '', capi_modo: cfg?.capi_modo || 'desligado', capi_test_event_code: cfg?.capi_test_event_code || '',
    page_id: cfg?.page_id || '', instagram_id: cfg?.instagram_id || '', whatsapp_numero: cfg?.whatsapp_numero || '',
    teto_orcamento_diario: cfg?.teto_orcamento_diario ?? '', max_variacao_pct: cfg?.max_variacao_pct ?? 30,
    automacao_ligada: !!cfg?.automacao_ligada, max_acoes_auto_dia: cfg?.max_acoes_auto_dia ?? 5,
  }));
  const [resultado, setResultado] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const salvar = useMutation({
    mutationFn: () => api.put('/trafego/config', form),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['trafego-config'] }); setForm((f) => ({ ...f, access_token: '' })); setResultado({ ok: true, texto: r.data.data.conta ? `Conectado à conta "${r.data.data.conta.nome}" (${r.data.data.conta.moeda}).` : 'Salvo.' }); },
    onError: (err) => setResultado({ ok: false, texto: erroMsg(err) }),
  });
  const testar = useMutation({
    mutationFn: () => api.post('/trafego/config/testar'),
    onSuccess: (r) => {
      const d = r.data.data;
      const ds = d.dataset_whatsapp;
      setResultado({ ok: true, texto: `Conta "${d.conta.nome}" ${d.conta.ativa ? 'ativa' : `com status ${d.conta.account_status}`}. ${ds && !ds.erro ? `Dataset do WhatsApp: ${ds}` : ds?.erro ? `Dataset do WhatsApp: ${ds.erro}` : ''}` });
      if (ds && !ds.erro && !form.dataset_id) setForm((f) => ({ ...f, dataset_id: ds }));
    },
    onError: (err) => setResultado({ ok: false, texto: erroMsg(err) }),
  });
  const sincronizar = useMutation({
    mutationFn: () => api.post('/trafego/sincronizar', { dias: 90 }, { timeout: 300000 }),
    onSuccess: (r) => { qc.invalidateQueries(); setResultado({ ok: true, texto: `Sincronizado: ${r.data.data.campanhas} campanhas, ${r.data.data.linhas_insights} linhas dos últimos 90 dias.` }); },
    onError: (err) => setResultado({ ok: false, texto: erroMsg(err) }),
  });

  const campo = (label, k, extra = {}) => (
    <label className="block">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <input className="input-field mt-1" value={form[k]} onChange={set(k)} {...extra} />
    </label>
  );

  const conectar = useMutation({
    mutationFn: () => api.get('/trafego/meta/conectar'),
    onSuccess: (r) => { window.location.href = r.data.data.url; },
    onError: (err) => setResultado({ ok: false, texto: erroMsg(err) }),
  });
  const [avancado, setAvancado] = useState(false);
  const contas = cfg?.contas_disponiveis || [];
  const diasVence = cfg?.token_expira_em ? Math.ceil((new Date(cfg.token_expira_em) - Date.now()) / 86400000) : null;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
        <h3 className="font-semibold text-gray-800">Facebook / Meta</h3>
        {cfg?.token_mascarado ? (
          <div className="text-sm bg-green-50 border border-green-100 rounded-lg p-3 text-green-800">
            Conectado{cfg.conectado_por ? <> por <strong>{cfg.conectado_por}</strong></> : ''}{cfg.token_origem === 'manual' ? ' (token colado manualmente)' : ''}.{' '}
            {diasVence === null ? 'O acesso não vence.' : diasVence > 0 ? `O acesso vence em ${diasVence} dia(s) — reconecte antes disso.` : 'O acesso VENCEU — conecte de novo.'}
          </div>
        ) : (
          <p className="text-sm text-gray-600">Clique em conectar, entre com o Facebook de quem administra a inBase e marque a <strong>página</strong> e a <strong>conta de anúncios</strong> da inBase.</p>
        )}
        {!cfg?.app_meta_ok && <div className="text-xs text-red-600">O app da Meta não está configurado neste servidor (META_APP_ID/META_APP_SECRET).</div>}
        <button onClick={() => conectar.mutate()} disabled={conectar.isPending || !cfg?.app_meta_ok}
          className="flex items-center gap-2 bg-[#1877F2] text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
          {conectar.isPending ? 'Abrindo o Facebook…' : cfg?.token_mascarado ? 'Reconectar com Facebook' : 'Conectar com Facebook'}
        </button>

        {contas.length > 0 ? (
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Conta de anúncios que o gestor vai acompanhar</span>
            <select className="input-field mt-1" value={form.ad_account_id} onChange={set('ad_account_id')}>
              <option value="">— escolha —</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}{c.empresa ? ` · ${c.empresa}` : ''}{c.ativa ? '' : ' (inativa)'}</option>)}
            </select>
          </label>
        ) : cfg?.token_mascarado && cfg?.token_origem === 'login' ? (
          <div className="text-xs text-yellow-700 bg-yellow-50 rounded-lg p-2">A autorização não liberou nenhuma conta de anúncios — reconecte e marque a conta da inBase.</div>
        ) : null}
        {(cfg?.paginas || []).length > 0 && (
          <p className="text-xs text-gray-500">Páginas liberadas: {cfg.paginas.map((p) => p.nome).join(', ')}</p>
        )}

        <h3 className="font-semibold text-gray-800 pt-2">Conversões (CAPI)</h3>
        {campo('ID do dataset (Gerenciador de Eventos)', 'dataset_id', { placeholder: 'preenchido pelo "Testar conexão"' })}
        {campo('ID da conta do WhatsApp Business (WABA)', 'waba_id')}
        <label className="block">
          <span className="text-xs font-medium text-gray-600">Envio de conversões para a Meta</span>
          <select className="input-field mt-1" value={form.capi_modo} onChange={set('capi_modo')}>
            <option value="desligado">Desligado (só monta a fila)</option>
            <option value="teste">Teste (Eventos de teste, não otimiza)</option>
            <option value="producao">Produção (envia de verdade)</option>
          </select>
        </label>
        {form.capi_modo === 'teste' && campo('Código de teste (Gerenciador de Eventos › Eventos de teste)', 'capi_test_event_code', { placeholder: 'TEST12345' })}

        <h3 className="font-semibold text-gray-800 pt-2">Anúncios e limites</h3>
        <div className="grid grid-cols-2 gap-3">
          {campo('ID da página do Facebook', 'page_id')}
          {campo('ID do Instagram', 'instagram_id')}
        </div>
        {campo('WhatsApp dos anúncios (Iago, com 55 e DDD)', 'whatsapp_numero', { placeholder: '554497529426' })}
        <div className="grid grid-cols-2 gap-3">
          {campo('Teto de orçamento diário da conta (R$)', 'teto_orcamento_diario', { type: 'number', min: 0, placeholder: 'ex.: 150' })}
          {campo('Maior mudança de orçamento por vez (%)', 'max_variacao_pct', { type: 'number', min: 5, max: 100 })}
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!form.automacao_ligada} onChange={(e) => setForm((f) => ({ ...f, automacao_ligada: e.target.checked }))} />
          <span>Permitir que regras em modo <strong>automático</strong> executem sozinhas (só pausar ou reduzir orçamento). Exige teto definido.</span>
        </label>
        {form.automacao_ligada && campo('Máximo de ações automáticas por dia', 'max_acoes_auto_dia', { type: 'number', min: 1, max: 50 })}

        <button type="button" onClick={() => setAvancado((v) => !v)} className="text-xs text-gray-500 underline">{avancado ? 'Esconder' : 'Avançado: colar token / ID da conta manualmente'}</button>
        {avancado && (
          <div className="space-y-3 border-l-2 border-gray-100 pl-3">
            {campo('ID da conta de anúncios (act_…)', 'ad_account_id', { placeholder: 'act_1234567890' })}
            {campo(cfg?.token_mascarado ? `Token (salvo: ${cfg.token_mascarado} — deixe vazio para manter)` : 'Token do usuário do sistema', 'access_token', { type: 'password', placeholder: 'EAAG…', autoComplete: 'off' })}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60">{salvar.isPending ? 'Salvando…' : 'Salvar'}</button>
          <button onClick={() => testar.mutate()} disabled={testar.isPending || !cfg?.configurado} className="border border-gray-200 px-4 py-2 rounded-lg text-sm disabled:opacity-50">Testar conexão</button>
          <button onClick={() => sincronizar.mutate()} disabled={sincronizar.isPending || !cfg?.configurado} className="border border-gray-200 px-4 py-2 rounded-lg text-sm disabled:opacity-50">{sincronizar.isPending ? 'Puxando…' : 'Puxar últimos 90 dias'}</button>
        </div>
        {resultado && (
          <div className={`flex items-start gap-2 text-sm rounded-lg p-3 ${resultado.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            {resultado.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertTriangle size={16} className="mt-0.5" />} {resultado.texto}
          </div>
        )}
      </div>
      <div className="bg-gray-50 rounded-xl border border-gray-100 p-5 text-sm text-gray-700 space-y-3">
        <h3 className="font-semibold text-gray-800">Como funciona</h3>
        <ol className="list-decimal pl-5 space-y-1">
          <li><strong>Conectar com Facebook</strong> abre o login da Meta. Entre com quem administra a página e a conta de anúncios da inBase.</li>
          <li>Na tela da Meta, marque a <strong>página da inBase</strong> e a <strong>conta de anúncios</strong> — só o que você marcar fica visível aqui.</li>
          <li>De volta ao CRM, escolha a conta, clique em <strong>Salvar</strong> e depois em <strong>Puxar últimos 90 dias</strong>.</li>
        </ol>
        <p className="text-xs text-gray-500">O acesso fica guardado criptografado. O CRM lê a conta todo dia e devolve conversões. Mudanças (pausar, orçamento, campanha nova) só acontecem pela aba <strong>Ações</strong>, com aprovação de um admin — campanha nova nasce pausada — ou por regra automática, se você ligar a automação acima.</p>
        <p className="text-xs text-gray-500">Endereço de retorno que precisa estar cadastrado no app da Meta: <code className="break-all">{cfg?.url_retorno}</code></p>
      </div>
    </div>
  );
}

// ── Visão geral (Meta + Google) ─────────────────────────────────────────────
function VisaoGeral({ isAdmin, irPara }) {
  const funil = useQuery({ queryKey: ['trafego-funil', 30], queryFn: () => api.get('/trafego/funil', { params: { dias: 30 } }).then(r => r.data.data) });
  const pend = useQuery({ queryKey: ['trafego-acoes', 'pendentes'], queryFn: () => api.get('/trafego/acoes?status=pendentes').then(r => r.data.data) });
  const goo = useQuery({ queryKey: ['google', 'status'], queryFn: () => api.get('/trafego/google').then(r => r.data.data) });
  const sites = goo.data?.sites || [];
  const siteSeo = sites.find((s) => s.verificado);
  const siteGa = sites.find((s) => s.ga4_property);
  const seo = useQuery({ queryKey: ['google', 'seo', siteSeo?.id, 28], enabled: !!siteSeo, queryFn: () => api.get(`/trafego/google/sites/${siteSeo.id}/seo`, { params: { dias: 28 } }).then(r => r.data.data) });
  const ga = useQuery({ queryKey: ['google', 'ga', siteGa?.id, 28], enabled: !!siteGa, queryFn: () => api.get(`/trafego/google/sites/${siteGa.id}/analytics`, { params: { dias: 28 } }).then(r => r.data.data) });
  const t = funil.data?.totais;
  const lim = pend.data?.limites;
  const nPend = pend.data?.acoes?.length || 0;

  const alertas = [];
  if (lim?.conta?.problemas?.length) alertas.push({ cor: 'red', texto: `Meta: a conta de anúncios não está entregando — ${lim.conta.problemas.join('; ')}.` });
  if (nPend) alertas.push({ cor: 'blue', texto: `${nPend} ação(ões) aguardando aprovação na Meta.`, ir: 'acoes' });
  if (goo.data && !goo.data.conectado) alertas.push({ cor: 'yellow', texto: 'Google ainda não conectado.', ir: 'google' });
  sites.filter((s) => !s.verificado).forEach((s) => alertas.push({ cor: 'yellow', texto: `Google: ${s.dominio} ainda não foi verificado no Search Console.`, ir: 'google' }));
  if (siteSeo && seo.data && !seo.data.atual.impressoes) alertas.push({ cor: 'gray', texto: `Google: o Search Console de ${siteSeo.dominio} ainda não liberou dados (site recém-verificado leva até ~1 dia).` });
  if (goo.data?.conectado && !siteGa) alertas.push({ cor: 'yellow', texto: 'Nenhum site com Google Analytics ligado — sem ele não dá para ver de onde vêm as visitas do site.', ir: 'google' });
  const COR = { red: 'bg-red-50 border-red-200 text-red-800', yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800', blue: 'bg-blue-50 border-blue-200 text-blue-800', gray: 'bg-gray-50 border-gray-200 text-gray-700' };

  const Bloco = ({ titulo, sub, ir, children }) => (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h3 className="font-semibold text-gray-800">{titulo}</h3><span className="text-xs text-gray-400">{sub}</span>
        {ir && <button onClick={() => irPara(ir)} className="ml-auto text-xs text-primary underline">ver detalhes</button>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-6">
      {alertas.length > 0 && (
        <div className="space-y-2">
          {alertas.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 border rounded-xl px-4 py-2 text-sm ${COR[a.cor]}`}>
              <AlertTriangle size={15} className="shrink-0" /> <span>{a.texto}</span>
              {a.ir && <button onClick={() => irPara(a.ir)} className="ml-auto text-xs underline whitespace-nowrap">abrir</button>}
            </div>
          ))}
        </div>
      )}

      <Bloco titulo="Meta Ads" sub="Facebook e Instagram · últimos 30 dias" ir="painel">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Investimento" value={t ? fmtBRL(t.investimento) : '—'} sub={t ? `${fmtNum(t.impressoes)} impressões` : ''} destaque />
          <Kpi label="Conversas / leads" value={t ? fmtNum(t.conversas_meta + t.leads_meta) : '—'} sub={t?.custo_conversa_meta ? `${fmtBRL(t.custo_conversa_meta)} cada` : ''} />
          <Kpi label="Visitas agendadas (de anúncio)" value={t ? fmtNum(t.visitas) : '—'} sub={t?.custo_visita ? `${fmtBRL(t.custo_visita)} por visita` : ''} />
          <Kpi label="Vendas (de anúncio)" value={t ? fmtNum(t.vendas) : '—'} sub={t?.vendas ? `VGV ${fmtBRL(t.vgv)}` : ''} />
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="bg-white border border-gray-100 rounded-lg px-3 py-1">Orçamento ativo: <strong>{lim?.orcamento_ativo != null ? `${fmtBRL(lim.orcamento_ativo)}/dia` : '—'}</strong></span>
          <span className="bg-white border border-gray-100 rounded-lg px-3 py-1">Teto: <strong>{lim?.teto_orcamento_diario ? `${fmtBRL(lim.teto_orcamento_diario)}/dia` : 'não definido'}</strong></span>
          {lim?.conta?.saldo != null && <span className="bg-white border border-gray-100 rounded-lg px-3 py-1">Saldo pré-pago: <strong>{fmtBRL(lim.conta.saldo)}</strong></span>}
        </div>
      </Bloco>

      <Bloco titulo="Google" sub={siteSeo ? `busca orgânica de ${siteSeo.dominio} · 28 dias` : 'Search Console e Analytics'} ir="google">
        {!goo.data?.conectado ? (
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-sm text-gray-600">Conecte o Google na seção <button onClick={() => irPara('google')} className="text-primary underline">Google</button> para ver buscas e visitas dos sites.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Cliques no Google" value={seo.data ? fmtNum(seo.data.atual.cliques) : '—'} sub={seo.data ? `${fmtNum(seo.data.atual.impressoes)} vezes que apareceu` : siteSeo ? 'carregando…' : 'nenhum site verificado'} destaque />
            <Kpi label="Posição média" value={seo.data?.atual.posicao_media ?? '—'} sub={seo.data?.atual.ctr != null ? `CTR ${seo.data.atual.ctr}%` : ''} />
            <Kpi label="Visitas ao site" value={ga.data ? fmtNum(ga.data.totais.sessoes) : '—'} sub={siteGa ? `${siteGa.dominio} · Analytics` : 'Analytics não ligado'} />
            <Kpi label="Conversões no site" value={ga.data ? fmtNum(ga.data.totais.eventos_chave) : '—'} sub={ga.data?.canais?.[0] ? `maior canal: ${ga.data.canais[0].canal}` : ''} />
          </div>
        )}
        <div className="flex flex-wrap gap-2 text-xs">
          {sites.map((s) => (
            <span key={s.id} className="bg-white border border-gray-100 rounded-lg px-3 py-1">{s.dominio}: {s.verificado ? 'Search Console ✓' : 'falta verificar'} · {s.ga4_property ? 'Analytics ✓' : 'sem Analytics'}</span>
          ))}
          <PerfilEmpresaSelo />
        </div>
      </Bloco>

      <div className="grid md:grid-cols-3 gap-3">
        {[
          ['agente', Bot, 'Gestor IA', 'Pergunte sobre Meta, Google, públicos e criativos — ele propõe campanhas para você aprovar.'],
          ['criativos', Film, 'Criativos', 'Artes e vídeos do Drive e da conta Meta, com o desempenho de cada uma.'],
          ['acoes', ListChecks, 'Ações e regras', 'Aprovar o que o gestor propôs, histórico com desfazer e regras automáticas com teto.'],
        ].map(([k, Icon, titulo, texto]) => (
          <button key={k} onClick={() => irPara(k)} className="text-left bg-white rounded-xl border border-gray-100 p-4 hover:border-primary/40">
            <div className="flex items-center gap-2 font-semibold text-gray-800"><Icon size={16} /> {titulo}</div>
            <p className="text-xs text-gray-500 mt-1">{texto}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function PerfilEmpresaSelo() {
  const { data: st } = useQuery({ queryKey: ['gperfil', 'status'], queryFn: () => api.get('/trafego/google-perfil').then((r) => r.data.data) });
  const ok = st?.api_estado === 'liberada';
  return (
    <span className={`bg-white rounded-lg px-3 py-1 ${ok ? 'border border-gray-100' : 'border border-dashed border-gray-200 text-gray-500'}`}>
      Perfil da Empresa: {!st ? '…' : ok ? (st.local ? `${st.titulo || 'ligado'} ✓` : 'liberado — falta escolher o perfil') : 'aguardando aprovação do Google'}
    </span>
  );
}

// seções → sub-abas. As chaves das sub-abas são as mesmas de antes (links ?aba=… do login do Facebook/Google continuam valendo)
const SECOES = [
  { k: 'geral', label: 'Visão geral', icon: BarChart3, abas: [['geral', 'Visão geral']] },
  { k: 'meta', label: 'Meta Ads', icon: Target, abas: [['painel', 'Painel'], ['desempenho', 'Desempenho e metas'], ['acoes', 'Ações e regras'], ['criativos', 'Criativos'], ['conversoes', 'Conversões (CAPI)'], ['analises', 'Análises da IA']] },
  { k: 'google', label: 'Google', icon: Globe, abas: [['google', 'SEO e Analytics'], ['google_perfil', 'Perfil da Empresa'], ['google_ads', 'Google Ads']] },
  { k: 'ia', label: 'Gestor IA', icon: Bot, abas: [['agente', 'Conversa'], ['publico', 'Público por empreendimento']] },
  { k: 'config', label: 'Configuração', icon: Settings, admin: true, abas: [['config', 'Conexão Meta e limites']] },
];

export default function GestorTrafego() {
  const user = getUser();
  const isAdmin = user?.perfil === 'admin';
  const qc = useQueryClient();
  const params = new URLSearchParams(window.location.search);
  const secoes = SECOES.filter((x) => !x.admin || isAdmin);
  const existe = (k) => secoes.some((x) => x.abas.some(([a]) => a === k));
  const [aba, setAbaEstado] = useState(() => (existe(params.get('aba')) ? params.get('aba') : 'geral'));
  const setAba = (k) => {
    setAbaEstado(k);
    const u = new URL(window.location.href);
    u.searchParams.set('aba', k);
    window.history.replaceState(null, '', `${u.pathname}${u.search}`);
  };
  const secao = secoes.find((x) => x.abas.some(([a]) => a === aba)) || secoes[0];
  const [avisoMeta] = useState(() => (params.get('meta') ? { tipo: params.get('meta'), msg: params.get('msg') } : null));
  // o aviso da volta do Facebook vem no endereço: mostra uma vez e limpa (senão fica preso a cada recarga)
  React.useEffect(() => {
    if (!params.get('meta')) return;
    const u = new URL(window.location.href);
    u.searchParams.delete('meta'); u.searchParams.delete('msg');
    window.history.replaceState(null, '', `${u.pathname}${u.search}${u.hash}`);
  }, []);
  const [dias, setDias] = useState(30);
  const { data: cfg } = useQuery({
    queryKey: ['trafego-config'], enabled: isAdmin,
    queryFn: () => api.get('/trafego/config').then(r => r.data.data),
  });
  const sincronizar = useMutation({
    mutationFn: () => api.post('/trafego/sincronizar', { dias: Math.max(dias, 7) }, { timeout: 300000 }),
    onSuccess: () => qc.invalidateQueries(),
    onError: (err) => alert('Erro ao sincronizar: ' + erroMsg(err)),
  });
  const configurado = isAdmin ? !!cfg?.configurado : true;

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestor de Tráfego</h1>
          <p className="text-sm text-gray-500">Meta Ads e Google (busca e site) — do primeiro clique até o contrato assinado.</p>
        </div>
        {aba === 'painel' && (
          <div className="flex items-center gap-2">
            <select className="border border-gray-200 rounded-lg text-sm px-3 py-2" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
              <option value={7}>Últimos 7 dias</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={90}>Últimos 90 dias</option>
            </select>
            <button onClick={() => sincronizar.mutate()} disabled={sincronizar.isPending}
              className="flex items-center gap-2 border border-gray-200 bg-white px-3 py-2 rounded-lg text-sm disabled:opacity-60"
              title={cfg?.ultimo_sync_em ? `Última sincronização: ${fmtData(cfg.ultimo_sync_em)}` : ''}>
              <RefreshCw size={14} className={sincronizar.isPending ? 'animate-spin' : ''} /> Sincronizar
            </button>
          </div>
        )}
      </div>

      {avisoMeta && !(avisoMeta.tipo === 'erro' && cfg?.configurado) && (
        <div className={`rounded-xl p-3 text-sm border ${avisoMeta.tipo === 'conectado' ? 'bg-green-50 border-green-200 text-green-800' : avisoMeta.tipo === 'cancelado' ? 'bg-gray-50 border-gray-200 text-gray-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {avisoMeta.tipo === 'conectado' ? 'Facebook conectado. Confira a conta de anúncios abaixo, salve e puxe os últimos 90 dias.'
            : avisoMeta.tipo === 'cancelado' ? 'Conexão com o Facebook cancelada.' : `Não foi possível conectar: ${avisoMeta.msg || 'erro desconhecido'}`}
        </div>
      )}

      {isAdmin && cfg?.ultimo_sync_erro && secao.k === 'meta' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">Última sincronização da Meta com erro: {cfg.ultimo_sync_erro}</div>
      )}

      <div className="space-y-2">
        <div className="flex gap-1 overflow-x-auto">
          {secoes.map(({ k, label, icon: Icon, abas }) => (
            <button key={k} onClick={() => setAba(abas[0][0])}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap ${secao.k === k ? 'bg-primary text-white font-semibold' : 'text-gray-600 hover:bg-gray-100'}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        {secao.abas.length > 1 && (
          <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
            {secao.abas.map(([k, label]) => (
              <button key={k} onClick={() => setAba(k)}
                className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${aba === k ? 'border-primary text-primary font-semibold' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {aba === 'geral' && <VisaoGeral isAdmin={isAdmin} irPara={setAba} />}
      {aba === 'painel' && <Painel dias={dias} configurado={configurado} />}
      {aba === 'desempenho' && <Desempenho isAdmin={isAdmin} />}
      {aba === 'acoes' && <Acoes isAdmin={isAdmin} />}
      {aba === 'criativos' && <Criativos />}
      {aba === 'conversoes' && <Conversoes cfg={cfg} />}
      {aba === 'analises' && <Analises />}
      {aba === 'google' && <Google isAdmin={isAdmin} />}
      {aba === 'google_perfil' && <GooglePerfil isAdmin={isAdmin} />}
      {aba === 'google_ads' && <GoogleAds isAdmin={isAdmin} />}
      {aba === 'agente' && <GestorIA />}
      {aba === 'publico' && <Publico />}
      {aba === 'config' && isAdmin && cfg && <Configuracao cfg={cfg} />}
    </div>
  );
}
